import React from "react";
import cn from "classnames";
import { FormattedMessage, useIntl } from "react-intl";

import { ITask } from "../../../../common/types";
import { formatTime } from "../../../../common/helpers/formatTime";

import Icons from "../../../icons";
import Icon from "../../../icon";

import messages from "./messages";
import {
    TableScroller,
    Table,
    MoreInfo,
    TotalPlay,
    TotalFail,
    LegendCenter,
    Legend,
    LegendPlayed,
    LegendFailed,
    LegendFirstSuccess,
    LegendGaveUp,
    Difficulty,
} from "./styles";

import { IDifficulty, IProps } from "./interfaces";
import { getCompletionDate } from "./utils";

const ResultTable = ({ plan, results, isSimulation, studentCompleted, isExperience }: IProps) => {
    const { formatDate } = useIntl();
    let tasks: (ITask | null)[] = plan.tasks;
    const difficulties: IDifficulty[] = [];
    if (isExperience) {
        tasks = plan.tasks.map(task => {
            if (plan.experience.positions.indexOf(task.id) !== -1) {
                return task;
            } else {
                if ((plan.experience.positions.filter(position => Array.isArray(position)).reduce((all, next) => [...(all as number[]), ...(next as number[])], []) as number[])!.indexOf(task.id) !== -1) {
                    return task;
                }
            }
            return null;
        })
        plan.experience.positions.forEach(position => {
            if (Array.isArray(position)) {
                difficulties.push({
                    value: "Easy",
                    id: position[0],
                });
                difficulties.push({
                    value: "Hard",
                    id: position[1],
                });
            }
        })
    }
    return (
        <div>
            <TableScroller>
                <Table className={cn(isSimulation && "Simulation")}>
                    <thead>
                        <tr>
                            {!studentCompleted && (
                                <td className="first-column">
                                    <b>
                                        <FormattedMessage {...messages.name} />
                                    </b>
                                </td>
                            )}
                            {!isSimulation && (
                                <td style={{ whiteSpace: "nowrap" }}>
                                    <FormattedMessage {...messages.dateOfCompletion} />
                                </td>
                            )}
                            {tasks.map((task, index) => {
                                if (!task) return null;
                                const TaskIcon = Icons[task.type];
                                const difficulty = (difficulties.find(t => t.id === task.id) || { value: null }).value;
                                return (
                                    <td className={cn(results.length && "with-task-type")} key={index}>
                                        <div className={"icon"}>
                                            <TaskIcon active />
                                            {difficulty && (
                                                <Difficulty className={difficulty}>
                                                    <Icon i="chevron-right" />
                                                </Difficulty>
                                            )}
                                        </div>
                                    </td>
                                )
                            })}
                            <td className={cn(results.length && "with-task-type")}>
                                <b>
                                    <FormattedMessage {...messages.total} />
                                </b>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((result, index) => (
                            <tr key={index}>
                                {!studentCompleted && <td className="first-column">{result.name}</td>}
                                {!isSimulation && (
                                    <td
                                        className="date-column"
                                        title={!!result.date ? new Date(result.date).toLocaleDateString() : undefined}
                                    >
                                        {getCompletionDate(result.date, formatDate)}
                                    </td>
                                )}
                                {tasks.map((o, i) => {
                                    if (!o) return null;
                                    const taskResult = result.result[i];
                                    return (
                                        <td
                                            key={`${index}_${i}`}
                                            className={cn(i === 0 && "first-value", taskResult?.gaveUp && "gave-up-cell")}
                                        >
                                            {!taskResult ? "" : taskResult.time > 0 ? formatTime(taskResult.time) : "?"}
                                            {taskResult && (
                                                <MoreInfo>
                                                    {taskResult.play !== undefined && (
                                                        <TotalPlay>
                                                            <Icon i="play" /> {taskResult.play}
                                                        </TotalPlay>
                                                    )}
                                                    {taskResult.fail !== undefined && (
                                                        <TotalFail
                                                            className={
                                                                taskResult.fail === 0 && !taskResult.gaveUp
                                                                    ? "FirstCorrect"
                                                                    : ""
                                                            }
                                                        >
                                                            {taskResult.fail === 0 && !taskResult.gaveUp ? (
                                                                <Icon i="check" />
                                                            ) : (
                                                                <>
                                                                    <Icon i="times" /> {taskResult.fail}
                                                                </>
                                                            )}
                                                        </TotalFail>
                                                    )}
                                                </MoreInfo>
                                            )}
                                        </td>
                                    );
                                })}
                                {/* TODO: if not matching (the plan has been modifid) */}
                                <td>
                                    {formatTime(
                                        [...result.result].reduce(
                                            (total, num) => total + (!!num ? Math.max(0, num.time) : 0), 0
                                        )
                                    )}
                                    {result.result.length > 0 && (
                                        <MoreInfo>
                                            <TotalPlay>
                                                <Icon i="play" />{" "}
                                                {result.result.reduce((total, next) => total + next?.play || 0, 0)}
                                            </TotalPlay>
                                            <TotalFail
                                                className={
                                                    result.result.reduce((total, next) => total + next?.fail || 0, 0) === 0
                                                        ? "FirstCorrect"
                                                        : ""
                                                }
                                            >
                                                {result.result.reduce((total, next) => total + next?.fail || 0, 0) > 0 ? (
                                                    <>
                                                        <Icon i="times" />{" "}
                                                        {result.result.reduce((total, next) => total + next?.fail || 0, 0)}
                                                    </>
                                                ) : (
                                                    <Icon i="check" />
                                                )}
                                            </TotalFail>
                                        </MoreInfo>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableScroller>
            {!isSimulation && (
                <LegendCenter>
                    <Legend studentCompleted={!!studentCompleted}>
                        <LegendPlayed className="Legend">
                            <Icon i="play" /> <FormattedMessage {...messages.legendTotalPlayed} />
                        </LegendPlayed>
                        <LegendFailed className="Legend">
                            <Icon i="times" /> <FormattedMessage {...messages.legendTotalFailed} />
                        </LegendFailed>
                        <LegendFirstSuccess className="Legend">
                            <Icon i="check" /> <FormattedMessage {...messages.legendFirstSuccess} />
                        </LegendFirstSuccess>
                        <LegendGaveUp className="Legend">
                            {" "}
                            <FormattedMessage {...messages.gaveUp} />
                        </LegendGaveUp>
                    </Legend>
                </LegendCenter>
            )}
        </div>
    );
}

export default ResultTable;
